import { apiRequest } from '@/utils/api-request';
import { useOrgStore } from '@/stores/org-store';
import * as Sentry from '@sentry/react';
import { MCPServerConfig } from '@/pages/workflow/component/custom-node/generic-node/mcp-servers-input';

export interface AddMCPServerRequest {
  mcp_server_config: MCPServerConfig;
}

export interface AddMCPServerResponse {
  success: boolean;
  message: string;
  mcp_server_config?: MCPServerConfig;
  detail?: string;
}

/**
 * Adds a new MCP server configuration for the organization
 * @param mcpServerConfig The MCP server configuration to add
 * @returns Promise containing the response with success status and message
 */
export async function addMcpServer(
  mcpServerConfig: MCPServerConfig,
): Promise<AddMCPServerResponse> {
  const orgId = useOrgStore.getState().currentOrg?.orgid;

  if (!orgId) {
    throw new Error('Organization ID not found');
  }

  try {
    const response = await apiRequest<AddMCPServerResponse>({
      url: `/mcp-servers/${orgId}/add`,
      method: 'POST',
      target: 'workflow',
      body: {
        mcp_server_config: mcpServerConfig,
      },
    });

    if (response?.detail) {
      return {
        success: false,
        message: response.detail || 'Failed to add MCP server',
        detail: response.detail,
      };
    }

    // apiRequest wraps the response in a 'data' property
    // The actual response from backend is in response.data
    const backendResponse = response.data || response;

    if (!backendResponse) {
      return {
        success: false,
        message: 'No response from server',
        detail: 'No response from server',
      };
    }

    return backendResponse as AddMCPServerResponse;
  } catch (error) {
    console.error('Error adding MCP server:', error);
    Sentry.captureException(error, {
      extra: { context: 'add-mcp-server' },
    });
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error occurred';
    throw new Error(`Failed to add MCP server: ${errorMessage}`);
  }
}
